/**
 * DraaidagPil — één beeldtaal voor een draaidag in het Materialen-tabblad:
 * de pil in de kalender (klantlogo + korte titel), de tooltip-kaart erboven
 * (titel, status-chip, klant, crew, projectnummer) en de status-chip in de lijst.
 *
 * Waarom een eigen module: kalender (twee varianten: met en zonder extra dag) en
 * lijst tekenden elk hun eigen statuskleuren en logo, en liepen uit de pas. Wiki-
 * feedback Joris (30 sep 2026): "voelt als een ander interface dan de andere
 * overzichten in Toolframe". Kleur betekent hier alleen reserveringsstatus; de
 * meest voorkomende toestand ("Open") is bewust de rustigste.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import LogoMetTerugval from '../LogoMetTerugval';
import { DUBBEL_TOOLTIP } from '../../utils/dubbeleDraaidagen';

const FONT_KOP = '"Manrope", system-ui, sans-serif';
const FONT_BODY = '"Inter", system-ui, sans-serif';

/** Reserveringsstatus → kleuren. bg/fg voor de pil, dot/chip voor de status-chip. */
export const RESERVERING_STATUS = {
  Gereserveerd: { bg: '#dcefe6', fg: '#1f5f45', dot: '#10b981', chip: '#10b98114', chipTekst: '#1f6b4a' },
  'Niet nodig': { bg: '#dcefe6', fg: '#1f5f45', dot: '#10b981', chip: '#10b98114', chipTekst: '#1f6b4a' },
  Open: { bg: '#f3ede8', fg: '#5b4636', dot: '#d4943a', chip: '#d4943a14', chipTekst: '#8a5a1f' },
  Dubbel: { bg: '#fbe1e0', fg: '#8f1d1b', dot: '#dc2626', chip: '#dc262614', chipTekst: '#a83836' },
  'Losse reservering': { bg: '#fcf0c8', fg: '#5e4a06', dot: '#eab308', chip: '#eab30820', chipTekst: '#8a6d08' },
};
RESERVERING_STATUS.Los = RESERVERING_STATUS['Losse reservering'];

export const statusStijl = (status) => RESERVERING_STATUS[status] || RESERVERING_STATUS.Open;

/**
 * Pil-kleuren voor FullCalendar. `jouw` = de ingelogde gebruiker staat als camera op
 * deze draaidag: zelfde statuskleur, maar met een donkere rand links (geen tweede
 * kleurenset — dat maakte het scherm druk).
 */
export function pilKleuren(status, { jouw = false } = {}) {
  const s = statusStijl(status);
  return { backgroundColor: s.bg, color: s.fg, jouw, randKleur: jouw ? s.fg : null, statusKlasse: statusKlasse(status) };
}

/** CSS-klasse per status ("pil-gereserveerd", "pil-niet-nodig", …) voor de jouw-draaidag-rand. */
export const statusKlasse = (status) => `pil-${String(RESERVERING_STATUS[status] ? status : 'Open').toLowerCase().replace(/[^a-z]+/g, '-')}`;

/**
 * Rand links voor "jouw draaidag" in de tekstkleur van de status. Expliciet per status:
 * FullCalendar zet de tekstkleur op .fc-event-main, dus currentColor op .fc-event klopt niet.
 */
export const JOUW_RAND_CSS = Object.fromEntries(Object.entries(RESERVERING_STATUS).map(([st, k]) => [
  `& .fc-event.jouw-draaidag.${statusKlasse(st)}`,
  { borderLeft: `3px solid ${k.fg} !important`, borderRadius: '2px 6px 6px 2px !important', fontWeight: '700 !important' },
]));

/** Kleine chip met stip: "● Gereserveerd". */
export function ReserveringStatusChip({ status, breedte }) {
  if (!status) return null;
  const s = statusStijl(status);
  return (
    <Box data-testid="reservering-status-chip" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25,
      borderRadius: '0.35rem', bgcolor: s.chip, flexShrink: 0, ...(breedte && { width: breedte }) }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: s.dot, flexShrink: 0 }} />
      <Typography component="span" sx={{ fontFamily: FONT_BODY, fontSize: '0.64rem', fontWeight: 600, whiteSpace: 'nowrap', color: s.chipTekst }}>
        {status}
      </Typography>
    </Box>
  );
}

const Symbool = ({ name, kleur }) => (
  <span className="material-symbols-outlined" aria-hidden="true"
    style={{ fontSize: 14, color: kleur, fontVariationSettings: "'FILL' 0, 'wght' 400" }}>{name}</span>
);

const Regel = ({ icoon, children, tekstKleur = '#556071', gewicht = 400 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
    {icoon}
    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.72rem', fontWeight: gewicht, color: tekstKleur }}>{children}</Typography>
  </Box>
);

/** MUI-Tooltip-props voor de witte kaart (zelfde als op de draaidagen-kalender). */
export const TOOLTIP_KAART_PROPS = {
  componentsProps: {
    tooltip: { sx: { bgcolor: '#fff', color: '#2d3335', borderRadius: '0.625rem', p: 1.5, maxWidth: 320,
      boxShadow: '0 8px 32px rgba(45,51,53,0.14), 0 1px 4px rgba(45,51,53,0.08)', border: '1px solid rgba(173,179,181,0.2)' } },
    arrow: { sx: { color: '#fff' } },
  },
  arrow: true,
  enterDelay: 300,
  placement: 'top',
};

/**
 * Tooltip-kaart: titel + status-chip, daaronder klant (met logo), crew, projectnummer,
 * aantal materialen en de extra dagen.
 * @param data     { title, originalTitle, resStatus, klantNaam, crew, crewNietGekoppeld, projectNum, materiaalCount, heeftExtraDagVoor, heeftExtraDagNa }
 * @param klant    { naam, beeldmerkUrl } (optioneel, uit de accountcache)
 */
export function DraaidagTooltipKaart({ data, klant }) {
  if (!data) return null;
  const klantNaam = klant?.naam || data.klantNaam;
  return (
    <Box data-testid="draaidag-tooltip-kaart" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 200 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        {/* Zelfde hiërarchie als de pil: wat/waar (max. 2 regels), crew · #nummer · PL eronder */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', fontFamily: FONT_BODY, fontSize: '0.8rem', lineHeight: PIL_REGELHOOGTE, color: '#2d3335' }}>
          <DraaidagOmschrijvingTekst hoofd={data.hoofd || data.title} labels={data.labels} meta={data.meta}
            hoofdSx={{ fontFamily: FONT_KOP, fontWeight: 700, fontSize: '0.85rem' }} />
        </Box>
        <ReserveringStatusChip status={data.resStatus} />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
        {klantNaam && (
          <Regel gewicht={600} icoon={(
            <LogoMetTerugval src={klant?.beeldmerkUrl} fallback={<Symbool name="apartment" kleur="#adb3b5" />}
              sx={{ width: 14, height: 14, borderRadius: '2px', objectFit: 'contain' }} />
          )}>{klantNaam}</Regel>
        )}
        {!data.meta && data.crew && <Regel icoon={<Symbool name="groups" kleur="#adb3b5" />}>{data.crew}</Regel>}
        {data.mogelijkDubbel && (
          <Regel tekstKleur="#b45309" icoon={<Symbool name="warning" kleur="#d97706" />}>{DUBBEL_TOOLTIP}</Regel>
        )}
        {data.crewNietGekoppeld && (
          <Regel tekstKleur="#b45309" icoon={<Symbool name="person_add" kleur="#d97706" />}>
            Nog koppelen in Airtable: {data.crewNietGekoppeld}
          </Regel>
        )}
        {!data.meta && data.projectNum && <Regel icoon={<Symbool name="tag" kleur="#adb3b5" />}>Project {data.projectNum}</Regel>}
        {data.materiaalCount > 0 && (
          <Regel icoon={<Symbool name="inventory_2" kleur="#adb3b5" />}>{data.materiaalCount} materialen</Regel>
        )}
        {(data.heeftExtraDagVoor || data.heeftExtraDagNa) && (
          <Regel icoon={<Symbool name="date_range" kleur="#adb3b5" />}>
            {[data.heeftExtraDagVoor && 'dag vóór', data.heeftExtraDagNa && 'dag ná'].filter(Boolean).join(' + ')} erbij
          </Regel>
        )}
      </Box>
    </Box>
  );
}

/** Lettergrootte + regelhoogte van de pil én de lijst (feedback Joris 30 sep: "een tandje groter"). */
export const PIL_FONT = '0.84rem';
export const PIL_REGELHOOGTE = 1.3;

/** Titel mag over twee regels; pas daarna een ellipsis (feedback Joris 30 sep). */
export const PIL_TITEL_SX = {
  display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
  overflow: 'hidden', whiteSpace: 'normal', overflowWrap: 'anywhere',
  lineHeight: PIL_REGELHOOGTE, minWidth: 0, flex: 1,
};

/** Kleine tweede regel: crew · #nummer · PL (grijs, max. 1 regel) — feedback Joris 30 sep: hiërarchie. */
export const PIL_META_FONT = '0.75rem';
export const PIL_META_SX = {
  display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 1,
  overflow: 'hidden', whiteSpace: 'normal', overflowWrap: 'anywhere',
  fontSize: PIL_META_FONT, fontWeight: 500, lineHeight: PIL_REGELHOOGTE, opacity: 0.72, minWidth: 0,
};

/**
 * Label vóór de hoofdregel ("Optie", "Locatiebezoek"): in de tekstkleur van de pil, zonder
 * eigen kleur. Een optie krijgt een gestippelde rand (nog niet bevestigd, net als de
 * gearceerde optie in de Draaidagen-kalender).
 */
export function DraaidagLabel({ label }) {
  const optie = label === 'Optie';
  return (
    <Box component="span" data-testid="draaidag-label"
      sx={{ display: 'inline-block', mr: '5px', px: '4px', borderRadius: '4px', verticalAlign: '1px',
        fontSize: '0.66rem', fontWeight: 700, lineHeight: 1.35, letterSpacing: '0.02em', whiteSpace: 'nowrap',
        border: `1px ${optie ? 'dashed' : 'solid'} currentColor`, opacity: 0.8 }}>
      {label}
    </Box>
  );
}

/** Hoofdregel (labels + wat/waar, max. 2 regels) en eronder de meta-regel (max. 1 regel). */
export function DraaidagOmschrijvingTekst({ hoofd, labels = [], meta = '', hoofdSx = {} }) {
  return (
    <Box component="span" sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
      <Box component="span" data-testid="draaidag-pil-titel" sx={{ ...PIL_TITEL_SX, fontWeight: 600, ...hoofdSx }}>
        {labels.map((l) => <DraaidagLabel key={l} label={l} />)}
        {hoofd}
      </Box>
      {meta ? <Box component="span" data-testid="draaidag-pil-meta" sx={PIL_META_SX}>{meta}</Box> : null}
    </Box>
  );
}

/** Inhoud van de pil: klantlogo (als dat er is), waarschuwing externe crew, hoofdregel + meta. */
export function DraaidagPilInhoud({ titel, labels, meta, logoUrl, crewWaarschuwing = false, dubbelWaarschuwing = false }) {
  // Logo en stip staan bovenaan, gecentreerd op de eerste tekstregel.
  const eersteRegel = `calc(${PIL_REGELHOOGTE}em)`;
  return (
    <Box component="span" data-testid="draaidag-pil" sx={{ display: 'flex', alignItems: 'flex-start', gap: '5px', minWidth: 0, width: '100%', overflow: 'hidden' }}>
      <LogoMetTerugval src={logoUrl}
        sx={{ width: 14, height: 14, mt: `calc((${eersteRegel} - 14px) / 2)`, borderRadius: '2px', objectFit: 'contain', flexShrink: 0 }} />
      {crewWaarschuwing && (
        <Box component="span" title="Externe crew nog koppelen in Airtable"
          sx={{ width: 6, height: 6, mt: `calc((${eersteRegel} - 6px) / 2)`, borderRadius: '50%', bgcolor: '#d97706', flexShrink: 0 }} />
      )}
      {dubbelWaarschuwing && (
        <Box component="span" data-testid="draaidag-dubbel" title={DUBBEL_TOOLTIP} aria-label={DUBBEL_TOOLTIP}
          sx={{ flexShrink: 0, color: '#d97706', fontSize: '0.85em', lineHeight: PIL_REGELHOOGTE }}>⚠</Box>
      )}
      <DraaidagOmschrijvingTekst hoofd={titel} labels={labels} meta={meta} />
    </Box>
  );
}

// ── Extra dagen (−1 · draaidag · +1) per zichtbaar kalenderstuk ─────────────────
// FullCalendar knipt een event op een weekgrens in stukken en roept eventContent per stuk
// aan; in de "+n meer"-popover zelfs per dag. Alleen het eerste stuk krijgt de −1, alleen
// het laatste de +1; in de popover staat per dag precies één onderdeel. Breedtes volgen de
// kolommen: za/zo zijn smaller (6%) dan werkdagen (Codex-review 30 sep).
const isoDag = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const WEEKEND_BREEDTE = 6;
const WERKDAG_BREEDTE = (100 - 2 * WEEKEND_BREEDTE) / 5;
const kolomBreedte = (d) => ((d.getDay() === 0 || d.getDay() === 6) ? WEEKEND_BREEDTE : WERKDAG_BREEDTE);
const maandagVan = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

/** Onderdelen van één zichtbaar stuk: { voor, hoofd, na } als relatieve breedtes (0 = weg). */
export function extraDagenSegment({ startStr, endStr, isStart, isEnd, heeftVoor, heeftNa, inPopover, zichtbareDagen }) {
  const startD = new Date(`${startStr}T12:00:00`);
  const endD = new Date(`${endStr}T12:00:00`); // exclusief
  if (inPopover) {
    // Popover = één dag: isStart/isEnd zeggen of dit de eerste/laatste dag is.
    if (heeftVoor && isStart) return { voor: 1, hoofd: 0, na: 0 };
    if (heeftNa && isEnd) return { voor: 0, hoofd: 0, na: 1 };
    return { voor: 0, hoofd: 1, na: 0 };
  }
  // Kalenderdagen optellen met setDate (niet in ms): zomer-/wintertijd schuift anders het uur.
  const plusDagen = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  const laatsteDag = plusDagen(endD, -1);
  // Gemeten stuk (de dagen waar FullCalendar dit stuk echt tekent — het knipt ook binnen
  // een week, bv. bij "+n meer" in de maand): dat wint van de weekgrens-schatting.
  if (Array.isArray(zichtbareDagen) && zichtbareDagen.length) {
    const dagen = zichtbareDagen.map((iso) => new Date(`${iso}T12:00:00`));
    const voorIso = heeftVoor ? startStr : null;
    const naIso = heeftNa ? isoDag(laatsteDag) : null;
    let voor = 0; let na = 0; let hoofd = 0;
    dagen.forEach((d) => {
      const iso = isoDag(d);
      if (iso === voorIso) voor += kolomBreedte(d);
      else if (iso === naIso) na += kolomBreedte(d);
      else hoofd += kolomBreedte(d);
    });
    return { voor, hoofd, na };
  }
  const segStart = isStart ? startD : maandagVan(laatsteDag);
  const segEind = isEnd ? endD : plusDagen(maandagVan(startD), 7);
  const dagen = [];
  for (let d = new Date(segStart); isoDag(d) < isoDag(segEind) && dagen.length < 7; d = plusDagen(d, 1)) dagen.push(d);
  if (!dagen.length) dagen.push(segStart);
  const voorDag = heeftVoor && isStart ? dagen[0] : null;
  const naDag = heeftNa && isEnd && dagen.length > (voorDag ? 1 : 0) ? dagen[dagen.length - 1] : null;
  const voor = voorDag ? kolomBreedte(voorDag) : 0;
  const na = naDag ? kolomBreedte(naDag) : 0;
  const hoofd = dagen.filter((d) => d !== voorDag && d !== naDag).reduce((som, d) => som + kolomBreedte(d), 0);
  return { voor, hoofd, na };
}

